import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Flag, Clock, CheckCircle2, Grid3X3, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Logo, Spinner, Empty } from "@/components/Brand";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const pad = (n) => String(n).padStart(2, "0");
const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

const NumGrid = ({ qs, answers, flags, idx, go }) => (
  <div className="grid grid-cols-5 gap-2 sm:grid-cols-6 lg:grid-cols-5" data-testid="question-grid">
    {qs.map((q, i) => {
      const cls = flags.includes(q.id) ? "bg-amber-500 text-slate-950 font-bold" : answers[q.id] ? "bg-emerald-600 text-white font-bold" : "bg-slate-100 text-slate-700 border border-slate-300";
      return (
        <button key={q.id} onClick={() => go(i)} data-testid={`grid-num-${i + 1}`}
          className={`h-10 rounded text-sm transition-transform hover:scale-105 ${cls} ${i === idx ? "ring-2 ring-[#0A192F] ring-offset-2" : ""}`}>{i + 1}</button>
      );
    })}
  </div>
);

export default function Exam() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flags, setFlags] = useState([]);
  const [left, setLeft] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [gridOpen, setGridOpen] = useState(false);
  const [saving, setSaving] = useState(0);
  const offset = useRef(0);
  const finishing = useRef(false);

  const finish = useCallback(async (auto = false) => {
    if (finishing.current) return;
    finishing.current = true;
    try {
      await api.post(`/exam/sessions/${id}/finish`);
      toast.success(auto ? "Waktu habis. Ujian diselesaikan otomatis." : "Ujian selesai");
      nav(`/hasil/${id}`, { replace: true });
    } catch (e) {
      finishing.current = false;
      toast.error(`Gagal menyelesaikan ujian: ${errMsg(e)}. Mencoba lagi...`);
      if (auto) setTimeout(() => finish(true), 3000);
    }
  }, [id, nav]);

  useEffect(() => {
    let alive = true;
    api.get(`/exam/sessions/${id}`).then(({ data }) => {
      if (!alive) return;
      if (data.status === "finished") return nav(`/hasil/${id}`, { replace: true });
      offset.current = new Date(data.server_now).getTime() - Date.now();
      setS(data);
      setAnswers(data.answers || {});
      setFlags(data.flags || []);
      const firstOpen = data.questions.findIndex((q) => !data.answers?.[q.id]);
      setIdx(firstOpen > 0 ? firstOpen : 0);
    }).catch((e) => alive && setErr(errMsg(e)));
    return () => {
      alive = false;
    };
  }, [id, nav]);

  useEffect(() => {
    if (!s) return undefined;
    const end = new Date(s.ends_at).getTime();
    const tick = () => {
      const ms = end - (Date.now() + offset.current);
      setLeft(ms);
      if (ms <= 0) finish(true);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [s, finish]);

  const send = async (payload, rollback) => {
    setSaving((n) => n + 1);
    try {
      await api.put(`/exam/sessions/${id}/answer`, payload);
    } catch (e) {
      rollback();
      if (e?.response?.status === 409) {
        toast.error("Ujian sudah selesai. Jawaban tidak dapat diubah.");
        nav(`/hasil/${id}`, { replace: true });
      } else toast.error(`Jawaban belum tersimpan: ${errMsg(e)}`);
    } finally {
      setSaving((n) => n - 1);
    }
  };

  const choose = (key) => {
    const q = s.questions[idx];
    const prev = answers[q.id];
    const next = prev === key ? null : key;
    setAnswers((a) => ({ ...a, [q.id]: next || undefined }));
    send({ question_id: q.id, answer: next }, () => setAnswers((a) => ({ ...a, [q.id]: prev })));
  };
  const toggleFlag = () => {
    const q = s.questions[idx];
    const on = !flags.includes(q.id);
    setFlags((f) => (on ? [...f, q.id] : f.filter((x) => x !== q.id)));
    send({ question_id: q.id, answer: answers[q.id] || null, flagged: on }, () => setFlags((f) => (on ? f.filter((x) => x !== q.id) : [...f, q.id])));
  };

  if (err) return <div className="mx-auto max-w-xl px-4 py-20"><Empty text={err} /></div>;
  if (!s) return <Spinner label="Menyiapkan ujian..." />;
  const q = s.questions[idx];
  const answered = s.questions.filter((x) => answers[x.id]).length;
  const go = (i) => { setIdx(i); setGridOpen(false); window.scrollTo({ top: 0 }); };
  const low = left !== null && left < 5 * 60 * 1000;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="sticky top-0 z-30 border-b border-amber-500/30 bg-[#0A192F] text-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-3 px-4">
          <div className="flex items-center gap-4"><div className="hidden sm:block"><Logo light to="#" /></div><span className="max-w-[140px] truncate text-sm font-semibold sm:max-w-xs" data-testid="exam-package-name">{s.package_name}</span></div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 font-mono text-lg font-bold ${low ? "animate-pulse border-red-400 bg-red-500/20 text-red-200" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`} data-testid="exam-timer"><Clock className="h-4 w-4" />{left === null ? "--:--:--" : fmt(left)}</span>
            <button onClick={() => setConfirm(true)} className="btn-gold rounded-lg px-4 py-2 text-sm" data-testid="exam-finish-btn">Selesai</button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-7xl gap-6 px-4 pb-24 pt-6 lg:grid-cols-[1fr_300px] lg:pb-6">
        <main className="rounded-xl bg-white p-5 shadow-sm sm:p-8" data-testid="exam-question-panel">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-amber-600" data-testid="exam-subject-name">{q.subject_name}</span>
              <h2 className="font-display text-xl font-bold text-[#0A192F]" data-testid="exam-question-number">Soal {idx + 1} dari {s.questions.length}</h2>
            </div>
            <span className="text-xs text-slate-500" data-testid="exam-save-status">{saving > 0 ? "Menyimpan..." : "Tersimpan otomatis"}</span>
          </div>
          {q.text && <p className="whitespace-pre-line text-base leading-relaxed text-slate-800" data-testid="exam-question-text">{q.text}</p>}
          {q.image && <img src={fileUrl(q.image)} alt="Gambar soal" className="mt-4 max-h-[360px] w-auto max-w-full rounded-lg border object-contain" data-testid="exam-question-image" />}
          <div className="mt-6 space-y-3">
            {q.options.map((o) => {
              const sel = answers[q.id] === o.key;
              return (
                <button key={o.key} onClick={() => choose(o.key)} data-testid={`exam-option-${o.key}`}
                  className={`flex w-full items-start gap-4 rounded-lg border-2 p-4 text-left transition-colors ${sel ? "border-[#0A192F] bg-[#0A192F]/5" : "border-slate-200 hover:border-amber-400"}`}>
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full font-bold ${sel ? "bg-[#0A192F] text-amber-300" : "bg-slate-100 text-slate-600"}`}>{o.key}</span>
                  <span className="flex-1 space-y-2 pt-1">
                    {o.text && <span className="block text-slate-800">{o.text}</span>}
                    {o.image && <img src={fileUrl(o.image)} alt={`Pilihan ${o.key}`} className="max-h-40 max-w-full rounded border object-contain" />}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t pt-5">
            <button disabled={idx === 0} onClick={() => go(idx - 1)} className="flex items-center gap-1 rounded-lg border px-4 py-2.5 text-sm font-semibold disabled:opacity-40" data-testid="exam-prev-btn"><ChevronLeft className="h-4 w-4" />Sebelumnya</button>
            <button onClick={toggleFlag} className={`flex items-center gap-1 rounded-lg px-4 py-2.5 text-sm font-bold ${flags.includes(q.id) ? "bg-amber-500 text-slate-950" : "border border-amber-500 text-amber-700"}`} data-testid="exam-flag-btn"><Flag className="h-4 w-4" />{flags.includes(q.id) ? "Ragu-ragu ✓" : "Tandai Ragu"}</button>
            {idx < s.questions.length - 1
              ? <button onClick={() => go(idx + 1)} className="flex items-center gap-1 rounded-lg bg-[#0A192F] px-4 py-2.5 text-sm font-semibold text-white" data-testid="exam-next-btn">Berikutnya<ChevronRight className="h-4 w-4" /></button>
              : <button onClick={() => setConfirm(true)} className="btn-gold flex items-center gap-1 rounded-lg px-4 py-2.5 text-sm" data-testid="exam-last-finish-btn"><CheckCircle2 className="h-4 w-4" />Selesaikan</button>}
          </div>
        </main>

        <aside className={`${gridOpen ? "fixed inset-0 z-40 overflow-y-auto bg-white p-5" : "hidden"} lg:static lg:block lg:rounded-xl lg:bg-white lg:p-5 lg:shadow-sm`}>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-bold text-[#0A192F]">Navigasi Soal</h3>
            <button className="lg:hidden" onClick={() => setGridOpen(false)} data-testid="grid-close-btn"><X className="h-5 w-5" /></button>
          </div>
          <NumGrid qs={s.questions} answers={answers} flags={flags} idx={idx} go={go} />
          <div className="mt-5 space-y-1.5 text-xs text-slate-600">
            <p><span className="mr-2 inline-block h-3 w-3 rounded bg-emerald-600" />Sudah dijawab ({answered})</p>
            <p><span className="mr-2 inline-block h-3 w-3 rounded bg-amber-500" />Ragu-ragu ({flags.length})</p>
            <p><span className="mr-2 inline-block h-3 w-3 rounded border bg-slate-100" />Belum dijawab ({s.questions.length - answered})</p>
          </div>
        </aside>
      </div>
      <button onClick={() => setGridOpen(true)} className="fixed bottom-5 right-5 z-30 flex items-center gap-2 rounded-full bg-[#0A192F] px-4 py-3 text-sm font-semibold text-white shadow-xl lg:hidden" data-testid="grid-open-btn"><Grid3X3 className="h-4 w-4" />{answered}/{s.questions.length}</button>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="finish-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Selesaikan ujian?</AlertDialogTitle>
            <AlertDialogDescription>
              Terjawab {answered} dari {s.questions.length} soal. {s.questions.length - answered > 0 && `${s.questions.length - answered} soal belum dijawab. `}
              {flags.length > 0 && `${flags.length} soal ditandai ragu-ragu. `}Setelah selesai, jawaban tidak dapat diubah.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="finish-cancel-btn">Kembali</AlertDialogCancel>
            <AlertDialogAction onClick={() => finish(false)} className="bg-[#0A192F]" data-testid="finish-confirm-btn">Ya, selesaikan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
